import React, { useContext, useMemo, useState } from 'react';
import { PageLayout, Separator, Text } from '../../../components';
import {
  MeetingListCardStyle,
  TranscriptFilterContainerStyle,
  TranscriptStyle,
} from '../styles';
import { UserContext } from '../../../context';
import { TranscriptsResponse } from '../../../types';
import { useNavigate } from 'react-router-dom';
import { E_Route } from '@repo-sapiens/constants';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../styles/date-range.css';

import { CardBase, TranscriptListItem } from '../components';
import { css } from '@emotion/css';
import { useQuery } from '@apollo/client';
import { format } from 'date-fns';
import { useMeeting } from '../../../features/meetings/hooks';
import { EActivityEvent } from '@repo-sapiens/data-access-models';
import { GET_TRANSCRIPTS } from '@repo-sapiens/graphql';
import { recordActivity } from '@repo-sapiens/helpers';
import { config } from '../../../config';
import {
  CalendarBlankIcon,
  MagnifyingGlassIcon,
} from '../../../components/icons';
import { trackEvent } from '../../../libs';
import { theme } from '@repo-sapiens/theme';

const next2Weeks = new Date();
next2Weeks.setDate(next2Weeks.getDate() + 14);

//type ValuePiece = Date | null;
//
//type Value = ValuePiece | [ValuePiece, ValuePiece];

export const TranscriptListScreen = () => {
  const { state: userState } = useContext(UserContext);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [keyword, setKeyword] = useState<string>('');
  const { data: transcriptData, loading } = useQuery<TranscriptsResponse>(
    GET_TRANSCRIPTS,
    {
      //fetchPolicy: 'network-only',
      //pollInterval: demoMode ? undefined : 5000,
      variables: {
        date: startDate ? new Date(startDate.toString()).getTime() : undefined,
        endDate: endDate ? new Date(endDate.toString()).getTime() : undefined,
        keyword,
      },
    }
  );

  const {
    allMeetings,
    externalMeetings,
    loading: meetingLoading,
  } = useMeeting(
    startDate ? new Date(startDate.toString()).getTime() : undefined,
    endDate ? new Date(endDate.toString()).getTime() : undefined
  );

  const navigate = useNavigate();

  const handleClick = (
    id: string,
    hasTranscript: boolean,
    internal: boolean
  ) => {
    if (userState?._id) {
      trackEvent(
        hasTranscript
          ? EActivityEvent.TranscriptOpened
          : EActivityEvent.MeetingPrepClicked,
        {
          app: 'web',
          meetingType: internal ? 'internal' : 'external',
        }
      );
      recordActivity(config.apiUrl, {
        data: {
          user: userState?._id,
          event: hasTranscript
            ? EActivityEvent.TranscriptOpened
            : EActivityEvent.MeetingPrepClicked,
          source: 'web',
          data: {
            transcriptId: id,
          },
        },
      });

      const url = hasTranscript
        ? `${E_Route.MeetingTranscript}/${id}` +
          (internal ? '?internal=yes' : '')
        : `${E_Route.Meeting}/${id}`;
      navigate(url);
    }
  };

  //const transcripts = transcriptData?.transcripts || [];

  const all = useMemo(() => {
    const result = [];
    const transcripts = transcriptData?.transcripts || [];
    // merge allMeetings and transcripts
    // sort by date
    const length = Math.max(allMeetings?.length || 0, transcripts?.length || 0);
    for (let i = 0; i < length; i++) {
      const item = allMeetings[i];
      if (item) {
        const meeting = externalMeetings?.find(
          (m) => m.meetingId === item.meetingId || m._id === item.meetingId
        );
        const isInternal = !meeting;
        const tran = transcripts.find((t) => t.meetingId === item.meetingId);
        result.push({
          title: item.title,
          meetingDate: item.start ? new Date(item.start) : undefined,
          meetingId: item.meetingId,
          transcriptId: tran ? tran._id : undefined,
          hasTranscript: !!tran?._id,
          id: tran?._id || item?.id,
          disabled: !tran?._id && isInternal, // disable if no transcript and internal meeting
          internal: isInternal,
        });
      } else {
        const tran = transcripts[i];
        if (tran) {
          result.push({
            title: tran.meetingTitle || 'Ad hoc meeting',
            meetingDate: new Date(tran.meetingDate || tran.created),
            meetingId: tran.meetingId,
            transcriptId: tran._id,
            hasTranscript: true,
            disabled: false,
            id: tran._id,
            internal: true,
          });
        }
      }
    }
    return result.sort((a, b) => {
      return (b.meetingDate?.getTime() || 0) - (a.meetingDate?.getTime() || 0);
    });
  }, [transcriptData?.transcripts, allMeetings, externalMeetings]);

  return (
    <PageLayout title="Meetings | Multiplayer">
      <div className={TranscriptStyle}>
        <div
          className={css`
            display: block;
            @media (max-width: 768px) {
              display: block;
            }
          `}
        >
          <Text.H3>Find all your previous Meeting transcripts here</Text.H3>
          <Separator.Horizontal spacing={4} />
        </div>
        <div className={TranscriptFilterContainerStyle}>
          <div
            className={css`
              width: 100%;
              display: flex;
              flex-direction: row;
              align-items: center;
              border-radius: 47.139px;
              border: 1px solid ${theme.colors.border.primary};
              height: 40px;
              padding: 0px 12px;
            `}
          >
            <MagnifyingGlassIcon
              fill="highlight/1"
              style={{
                width: '16px',
                height: '16px',
              }}
            />
            <input
              placeholder="Filter by keyword (Ex: 'John', 'Jane', 'Meeting')"
              className={css`
                outline: none;
                box-sizing: border-box;
                transition: border-color 0.15s;
                height: 40px;
                width: 90%;
                background-color: transparent;
                border-width: 0;
                padding: 0 16px;
                color: ${theme.colors.text.primary};
                &::placeholder {
                  color: ${theme.colors.text['text/40%']};
                }
              `}
              onChange={(e) => setKeyword(e.target.value)}
            />
          </div>
          <div
            className={css`
              width: 100%;
              display: flex;
              flex-direction: row;
              align-items: center;
              border-radius: 47.139px;
              border: 1px solid ${theme.colors.border.primary};
              height: 40px;
              padding: 0px 12px;
            `}
          >
            <CalendarBlankIcon
              fill="highlight/1"
              style={{
                width: '16px',
                height: '16px',
              }}
            />
            <DatePicker
              selectsRange={true}
              startDate={startDate}
              endDate={endDate}
              placeholderText="Select date range"
              onChange={(update) => {
                setStartDate(update[0]);
                setEndDate(update[1]);
              }}
              isClearable={true}
            />
          </div>
        </div>
        <Separator.Vertical spacing={4} />
        <CardBase className={MeetingListCardStyle}>
          {(loading || meetingLoading) && <Text.Body>Loading...</Text.Body>}
          {all.length > 0 && (
            <div>
              <Text.Body2XS color="darkGrey">UPCOMING</Text.Body2XS>
              {all
                .filter((t) => t.meetingDate && t.meetingDate > new Date())
                .map((t, idx, all) => (
                  <React.Fragment key={t.transcriptId || t.meetingId}>
                    <TranscriptListItem
                      title={t.title || 'Ad hoc meeting'}
                      hasTranscript={t.hasTranscript}
                      noLink={t.disabled || !t.id}
                      startTime={
                        t.meetingDate
                          ? format(
                              new Date(t.meetingDate),
                              'EEEE, MMMM d • hh:mm a'
                            )
                          : 'Unknown'
                      }
                      onClick={() => {
                        if (t.disabled) {
                          return;
                        }
                        if (!t.id) {
                          return;
                        }
                        handleClick(t.id, t.hasTranscript, t.internal);
                      }}
                    />
                    {idx !== all.length - 1 && (
                      <Separator.Horizontal border spacing={1} />
                    )}
                  </React.Fragment>
                ))}
              <Separator.Horizontal spacing={6} />
              <Text.Body2XS color="darkGrey">PAST</Text.Body2XS>
              {all
                .filter((t) => t.meetingDate && t.meetingDate <= new Date())
                .map((t, idx, all) => (
                  <React.Fragment key={t.transcriptId || t.meetingId}>
                    <TranscriptListItem
                      title={t.title || 'Ad hoc meeting'}
                      hasTranscript={t.hasTranscript}
                      filtered={!!keyword}
                      noLink={t.disabled || !t.id}
                      startTime={
                        t.meetingDate
                          ? format(
                              new Date(t.meetingDate),
                              'EEEE, MMMM d • hh:mm a'
                            )
                          : 'Unknown'
                      }
                      onClick={() => {
                        if (t.disabled) {
                          return;
                        }
                        if (!t.id) {
                          return;
                        }
                        handleClick(t.id, t.hasTranscript, t.internal);
                      }}
                    />
                    {idx !== all.length - 1 && (
                      <Separator.Horizontal border spacing={1} />
                    )}
                  </React.Fragment>
                ))}
            </div>
          )}
          {!all.length && !loading && (
            <Text.Body>Nothing to show you there</Text.Body>
          )}
        </CardBase>
      </div>
    </PageLayout>
  );
};
